import { useEffect, useMemo, useRef, useState } from "react";
import { Snackbar } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types.ts";
import type {
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { useCompanyTypes } from "@hooks/useCompanyTypes";
import { formatCompanyTypes } from "@pages/vendor-license-request/constants";
import { searchPortOperatorLicenses } from "@services/licensingApi";
import { useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { completeScreenNavigation } from "@telemetry/trackNavigation";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { useTranslation } from "react-i18next";
import {
  buildBodyFromFilters,
  formatDateTime,
  formatStatus,
  getCompanyTypeFilterField,
  getLicenseBadgeType,
  PORT_OPERATOR_LICENSES_FIELDS,
} from "./utils";

const LANG_BASE = "licensing";
const TEST_ID_BASE = "port-operator-licenses";

const LOCAL_STORAGE_PAGE_KEY = "port-operator-licenses-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "port-operator-licenses-page-size";
const LOCAL_STORAGE_FILTERS_KEY = "port-operator-licenses-filters";
const SEARCH_INQUIRY_ID = "licensing.port-operator-licenses.search";
const SEARCH_INQUIRY_CONTEXT = {
  module: "licensing",
  screen: "Port Operator Licenses",
  action: "search_port_operator_licenses",
  entityType: "license",
};

const PORT_OPERATOR_LICENSES_STATUSES = [
  "AWAITING_MANAGING_DIRECTOR_VERIFICATION",
  "AWAITING_MANAGING_DIRECTOR_APPROVAL",
  "AWAITING_UPLOAD_RECEIPT",
  "REJECTED_MANAGING_DIRECTOR_VERIFICATION",
  "REJECTED_BY_MANAGING_DIRECTOR",
  "APPROVED",
  "EXPIRED",
] as const;

interface PortOperatorLicensesProps {
  onIsLoading: (v: boolean) => void;
}

export const PortOperatorLicenses: React.FC<PortOperatorLicensesProps> = ({ onIsLoading }) => {
  const { t } = useTranslation();
  const { data: companyTypes = [] } = useCompanyTypes();

  const [filters, setFilters] = useState<FilterItem[]>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_FILTERS_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? parseInt(saved, 10) : 25;
  });

  const {
    data,
    isLoading: isLicensesLoading,
    isFetching: isLicensesFetching,
    isError: isLicensesError,
    isSuccess: isLicensesSuccess,
    dataUpdatedAt: licensesDataUpdatedAt,
    error,
  } = useQuery({
    queryKey: ["portOperatorLicenses", page, pageSize, filters],
    queryFn: () =>
      searchPortOperatorLicenses({
        page,
        pageSize,
        body: buildBodyFromFilters(filters),
      }),
  });
  useTrackPendingInquiry(SEARCH_INQUIRY_ID, {
    isFetching: isLicensesFetching,
    isLoading: isLicensesLoading,
    isError: isLicensesError,
    isSuccess: isLicensesSuccess,
    error,
    dataUpdatedAt: licensesDataUpdatedAt,
  });
  useEffect(() => {
    onIsLoading(isLicensesLoading);

    return () => onIsLoading(false);
  }, [onIsLoading, isLicensesLoading]);

  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);

  useEffect(() => {
    if (isLicensesLoading || error || hasTrackedInitialLoad.current) {
      return;
    }

    hasTrackedInitialLoad.current = true;
    completeScreenNavigation("Licenses");
    trackScreenLoad("Licenses", "Port Operator Licenses", startedAt.current);
  }, [error, isLicensesLoading]);

  const headers: Header[] = useMemo(() => {
    return Object.values(PORT_OPERATOR_LICENSES_FIELDS).map((field) => ({
      field,
      columnName: t(`${LANG_BASE}.headerLabels.${field}`),
    }));
  }, [t]);

  const filterFields: Record<string, FilterFieldDefinition> = useMemo(
    () => ({
      refOrderNumber: {
        label: t(`${LANG_BASE}.headerLabels.requestedId`),
        type: "text",
        minLength: 1,
      },
      companyName: {
        label: t(`${LANG_BASE}.headerLabels.companyName`),
        type: "text",
        minLength: 1,
      },
      companyType: getCompanyTypeFilterField(
        t(`${LANG_BASE}.headerLabels.companyType`),
        companyTypes,
      ),
      natureOfBusinessInPof: {
        label: t(`${LANG_BASE}.headerLabels.natureOfBusiness`),
        type: "text",
        minLength: 5,
      },
      tradingLicenseNumber: {
        label: t(`${LANG_BASE}.headerLabels.tradeLicense`),
        type: "text",
        minLength: 1,
      },
      createdAt: {
        label: t(`${LANG_BASE}.headerLabels.dateSubmitted`),
        type: "dateFromToWithoutTime",
      },
      status: {
        label: t(`${LANG_BASE}.headerLabels.status`),
        type: "autocomplete",
        options: PORT_OPERATOR_LICENSES_STATUSES.map((status) => ({
          label: t(`${LANG_BASE}.status-labels.${status}`),
          value: status,
        })),
      },
    }),
    [companyTypes, t],
  );

  const onApplyFilters = (filters: FilterItem[]) => {
    startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    setFilters(filters);
    localStorage.setItem(LOCAL_STORAGE_FILTERS_KEY, JSON.stringify(filters));
    setPage(1);
    localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, "1");
  };

  const onClearFilters = () => {
    if (filters.length) {
      startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    }
    setFilters([]);
    localStorage.removeItem(LOCAL_STORAGE_FILTERS_KEY);
  };

  const na = useMemo(() => t("common.notApplicable"), [t]);

  const items = data?.items ?? [];
  const formattedFormData = items.map((input) => ({
    id: input.id,
    requestedId: input.refOrderNumber ?? na,
    companyName: input.companyName ?? na,
    companyType: formatCompanyTypes(input.companyTypes) ?? na,
    natureOfBusiness: input.natureOfBusinessInPof ?? na,
    tradeLicense: input.tradingLicenseNumber ?? na,
    dateSubmitted: formatDateTime(input.createdAt),
    status: formatStatus(input.status, t) ?? na,
    badgeType: getLicenseBadgeType(input.status),
  }));

  if (error) {
    return <Snackbar open autoHideDuration={6000} message={(error as any)?.message ?? "Error"} />;
  }

  return (
    <div data-testid={TEST_ID_BASE}>
      <CustomDataTable
        testId={TEST_ID_BASE}
        actionName={t(`${LANG_BASE}.add-license`)}
        headers={headers}
        rows={formattedFormData}
        isLoading={isLicensesLoading}
        page={page}
        title={" "}
        pageSize={pageSize}
        totalCount={data?.totalElements ?? 0}
        canPreview={true}
        onPageChange={(newPage, newSize) => {
          if (newPage !== page || newSize !== pageSize) {
            startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
          }
          setPage(newPage);
          setPageSize(newSize);
          localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(newPage));
          localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(newSize));
        }}
        navigationLinkBase="license-details"
        navigationParam="id"
        pageSizeOptions={[5, 10, 15, 20, 25]}
        filters={filters}
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
        availableFields={filterFields}
      />
    </div>
  );
};

export default PortOperatorLicenses;
